/* eslint-disable no-empty */
import { gql } from '@apollo/client';
import { rest, graphql } from '@lib/api';
import { LISTING_ATTRIBUTES, LISTING_ATTRIBUTES_SHORT } from '@lib/fragments';
import { toArray } from '@lib/utils';

export const LISTING_QUERY = gql`
  ${LISTING_ATTRIBUTES}
  query GetListing($id: Int!) {
    listingById(listingId: $id) {
      ...Listing
    }
  }
`;

export const LISTINGS_QUERY = gql`
  ${LISTING_ATTRIBUTES_SHORT}
  query GetListings($ids: [Int!]) {
    listingsByIds(listingIds: $ids) {
      ...AbbreviatedListing
    }
  }
`;

export default class Listing {
  /**
   * Static method intended to hydrate objects in arr
   * with attributes defined in LISTINGS_QUERY
   * for those objects that have a `listingId` key
   *
   * Usage:
   *i
   *  const listings = [{ listingId: 123 }]
   *  Listing.hydrate(listings)
   *
   *  Returns... [{listingId: 123, address: '...', city: '...', etc}]
   *
   * @param {Array}
   * @returns {Array}
   */
  static async hydrate(arr) {
    const ids = [...new Set(arr.map((obj) => obj.listingId))];
    const { listingsByIds: listings } = await graphql(LISTINGS_QUERY, { ids: ids });

    return arr.map((obj) => ({
      ...obj,
      ...toArray(listings).find((l) => l.listingId === obj.listingId)
    }));
  }

  /**
   * @param {Integer} Listing ID
   */
  constructor(id) {
    this.id = id;
  }

  /**
   * Query all details for this listing
   * @returns {Object}
   */
  async details() {
    try {
      const { listingById } = await graphql(LISTING_QUERY, { id: this.id });
      return listingById;
    } catch (e) {
      console.error(e);
      return;
    }
  }

  /**
   * Increments and returns the value for a listing's totalViews
   * @returns {Integer}
   */
  async stats() {
    if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
      try {
        const response = await rest(`/listings/${this.id}/stats`, {
          method: 'patch'
        });
        const totalViews = response?.data?.totalViews;
        return totalViews;
      } catch (e) {
        console.error(e);
        return;
      }
    }
  }

  async interest(years) {
    try {
      const response = await rest(
        `/interest-rates/${years}yf`,
        {
          method: 'get'
        },
        true
      );

      const currentRate = response?.data?.currentRate;
      const lastUpdatedOn = response?.data?.lastUpdatedOn;

      return { currentRate, lastUpdatedOn };
    } catch (e) {
      console.error(e);
      return;
    }
  }

  /**
   * Returns up to three similar listing objects
   * @returns {Array}
   */
  async getSimilarListings(arr) {
    try {
      if (arr.length > 0) {
        const { listingsByIds: listings } = await graphql(LISTINGS_QUERY, { ids: arr });
        return listings;
      } else {
        return arr;
      }
    } catch (ex) {
      console.log(ex);
    }
  }

  /**
   * Takes in a url string and returns a cannonicalized version
   * @param {String}
   * @returns {String}
   */
  static canonicalize(string) {
    return string.replace(/^[, ]+|[, ]+$|[, ]+/g, '-');
  }
}
